<template lang="pug">
.room-schedule-tab
  .mb-3
  .d-flex.justify-content-start.align-items-center.mb-2
    .d-flex.justify-content-start.align-items-center
      span.current-date {{ year }}年{{ month }}月
      HelpIcon(popoverId="other-office-roomschedule-help" title="凡例")
        template(v-slot:text)
          span
            span.can 〇
            span ：空床有り
          br
          span
            span.cant ×
            span ：空床無し
          br
          span
            span.blank-space 空欄
            span ：未公開
    .d-flex.justify-content-end.flex-fill
      button.btn.move-month-btn.mr-2(type="button" :class="{ disable: disablePreviousMonth }" :disabled="disablePreviousMonth" @click="onPreviousMonth") ＜ 前の月
      button.btn.move-month-btn.mr-2(type="button" @click="onCurrentMonth") 今月
      button.btn.move-month-btn(type="button" @click="onNextMonth") 次の月 ＞
  RoomScheduleTable(v-model="otherOfficeRoomSchedules" :fields="fields" :isLoading="isLoading")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import OtherOfficeRoomSchedulesService, { OtherOfficeRoomSchedulesGetModel } from "@/commons/services/OtherOfficeRoomSchedules";

import RoomScheduleTable from "@/pc/containers/otherOfficeRoomSchedules/RoomScheduleTable.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * ### 仮押さえ／転床一覧タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    RoomScheduleTable
  }
})
export default class RoomScheduleTab extends Vue {
  /** 待機者ラベルサービス */
  @Inject("OtherOfficeRoomSchedulesService") otherOfficeRoomSchedulesService!: OtherOfficeRoomSchedulesService;

  /** テーブル列名 */
  fields: Array<string> = [];
  /** テーブル各行の情報 */
  otherOfficeRoomSchedules: Array<OtherOfficeRoomSchedulesGetModel> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** 表示対象年 */
  year: number = new Date().getFullYear();
  /** 表示対象月 */
  month: number = new Date().getMonth() + 1;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 前の月ボタンの非活性か否かを返す
   *
   * @return 非活性：true、活性：true
   */
  get disablePreviousMonth(): boolean {
    const today: Date = new Date();
    const currentDay: Date = new Date(this.year, this.month - 1); // monthは0始まりなので-1して使用する

    // 年が未来であれば非活性にならない
    if (today.getFullYear() < currentDay.getFullYear()) {
      return false;
    }
    // 年が同じであればかつ、月が未来であれば非活性にならない
    else if (today.getFullYear() == currentDay.getFullYear() && today.getMonth() < currentDay.getMonth()) {
      return false;
    }
    // その他であれば非活性(年が過去or同じ年かつ同じ月以下)
    else {
      return true;
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const fields: Array<string> = this.createFields();
    this.fields = fields;
    const otherOfficeRoomSchedules: Array<OtherOfficeRoomSchedulesGetModel> = await this.getOtherOfficeRoomSchedules();
    this.otherOfficeRoomSchedules = otherOfficeRoomSchedules;

    this.isLoading = false;

    return;
  }

  /**
   * ### テーブル列名を作成する
   *
   * - 日付の列は月毎に可変なので、当月の最終日分のfieldを作成する
   */
  public createFields(): Array<string> {
    const fields: Array<string> = [];
    // 当月の最終日を取得する。「来月の0日」を指定すると「当月の最終日」のDateを作れる
    const lastDay: number = new Date(this.year as number, this.month as number, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      fields.push(`${this.year}/${this.month}/${i}`);
    }
    return fields;
  }

  /**
   * ### 仮押さえ／転床情報を取得する
   *
   * - 過去は表示対象外にするため、開始日時を指定する
   * - ページングを考慮し、必要数分だけ取得する
   */
  public async getOtherOfficeRoomSchedules(): Promise<Array<OtherOfficeRoomSchedulesGetModel>> {
    const responce: Array<OtherOfficeRoomSchedulesGetModel> = await this.otherOfficeRoomSchedulesService.gets(userStore, this.year, this.month);
    return responce;
  }

  /**
   * ### 前の月ボタン押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを前の月に変更する
   * - 変更した年・月を画面表示する
   */
  public async onPreviousMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() - 1);
    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();
    return;
  }

  /**
   * ### 今月ボタン押下イベント
   *
   * - 現在のDateを作成
   * - 現在の年・月を画面表示する
   */
  public async onCurrentMonth(): Promise<void> {
    let toDay: Date = new Date();
    this.year = toDay.getFullYear();
    this.month = toDay.getMonth() + 1;
    await this.createView();
    return;
  }

  /**
   * ### 次の月押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを次の月に変更する
   * - 変更した年・月を画面表示する
   */
  public async onNextMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() + 1);
    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.room-schedule-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  .current-date {
    font-weight: bold;
    font-size: 22px;
  }
}
</style>
